import { computed, ref, toValue } from "vue"
import axios from "axios"
import { useQuery, useMutation } from "@tanstack/vue-query"
import { toast } from "@/toast"
import { queryClient } from "@/query.js"

const dateDifferenceInDays = (a, b) => {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
  const msPerDay = 1000 * 60 * 60 * 24
  return Math.floor((utc2 - utc1) / msPerDay)
}

const useSubscriptions = (id) => {
  const { isFetching, data: subscriptions, refetch } = useQuery({
    queryKey: ["fetchAllSubscriptions"],
    async queryFn() {
      const { data } = await axios.get("/partner/subscriptions")
      return data
    },
    placeholderData: () => ([]),
  })

  const normalizedSubscriptions = computed(() => {
    return subscriptions.value.map(({
      currentTermEnd,
      contractTermEnd,
      cancelledAt,
      trialEnd,
      fqdn,
      subscriptionStatus,
      poNumber,
      planId,
      ...rest
    }) => {
      const isMYPA = (planId || "").includes("paid_annually")
      const isMonthly = (planId || "").includes("month")
      const isScheduledCancel = isMonthly && subscriptionStatus === "non_renewing" && cancelledAt

      const blockingTimestamp = isMYPA ? (contractTermEnd ?? currentTermEnd) : currentTermEnd
      const daysUntilRenewal = blockingTimestamp
        ? dateDifferenceInDays(new Date(Date.now()), new Date(blockingTimestamp * 1000))
        : null

      const effectiveDays = isMonthly ? (daysUntilRenewal ?? 0) + 30 : (daysUntilRenewal ?? 0)
      const isCancellationBlocked
        = subscriptionStatus === "active"
          && daysUntilRenewal !== null
          && effectiveDays <= 30

      const deletionTimestamp = isMYPA && subscriptionStatus === "non_renewing"
        ? contractTermEnd
        : isScheduledCancel
          ? cancelledAt
          : currentTermEnd
      const remainingDaysLeft = dateDifferenceInDays(new Date(Date.now()), new Date(deletionTimestamp * 1000))

      return {
        ...rest,
        planId,
        fqdn,
        subscriptionStatus,
        nextRenewal: currentTermEnd
          ? new Date(currentTermEnd * 1000).toLocaleDateString()
          : "",
        trialDaysLeft: dateDifferenceInDays(new Date(Date.now()), new Date(trialEnd * 1000)),
        remainingDaysLeft,
        subdomain: fqdn?.match(/^[^.]+/)[0],
        isCancelled: subscriptionStatus === "non_renewing",
        isTrial: subscriptionStatus === "in_trial",
        orderNumber: poNumber,
        isCancellationBlocked,
        contractEndDate: isMYPA && contractTermEnd
          ? new Date(contractTermEnd * 1000).toLocaleDateString()
          : null,
      }
    })
  })
  const currentSubscription = computed(() =>
    normalizedSubscriptions.value?.find(sub => sub.id === toValue(id)) || ref({}))
  const isCompleteStack = computed(() => {
    return currentSubscription.value.stackStatus === "ready"
  })
  const inProgressStack = computed(() => {
    return currentSubscription.value.stackStatus === "not_ready"
  })
  const isFailedStack = computed(() => {
    return currentSubscription.value.stackStatus === "failed"
  })
  const isBlockedSanctionReviewStack = computed(() => {
    return currentSubscription.value.stackStatus === "blocked_sanction_review"
  })
  const inUnknownStack = computed(() => {
    return !(inProgressStack.value || isCompleteStack.value || isFailedStack.value || isBlockedSanctionReviewStack.value)
  })
  // I decided to use inProgress for every action instead of the booleans provided
  // by useMutations to make things simpler.
  const inProgress = ref(false)

  const changeOrderNumber = async () => {
    try {
      inProgress.value = true
      const { orderNumber } = currentSubscription.value
      await axios.patch(`/partner/subscriptions/${toValue(id)}/po_number`, { orderNumber })
      queryClient.removeQueries(["fetchAllSubscriptions"])
      toast.success("Your order number was successfully changed.")
    }
    catch (e) {
      toast.error(e)
    }
    finally {
      inProgress.value = false
    }
  }
  const onSuccess = (subscription) => {
    queryClient.setQueryData(["fetchAllSubscriptions"], (oldSubscriptions) => {
      return oldSubscriptions
        .map(sub => sub.id === subscription.id ? subscription : sub)
    })
  }
  const cancelSubscription = () => {
    const { mutateAsync } = useMutation({
      async mutationFn(cancelReasonCode) {
        const { data } = await axios.delete(
          `/partner/subscriptions/${toValue(id)}?subscription_cancel_reason_code=${cancelReasonCode}`,
        )
        return data
      },
      onSuccess,
    })

    return async (cancelReasonCode) => {
      try {
        inProgress.value = true
        await mutateAsync(cancelReasonCode)
        toast.success("Subscription has been cancelled.")
      }
      catch (e) {
        toast.error(e)
      }
      finally {
        inProgress.value = false
      }
    }
  }
  const restoreSubscription = () => {
    const { mutateAsync } = useMutation({
      async mutationFn() {
        await axios.post(`/partner/subscriptions/${toValue(id)}/reactivate`)
        const { data } = await axios.get(`/partner/subscriptions/${toValue(id)}`)
        return data
      },
      onSuccess,
    })

    return async () => {
      try {
        inProgress.value = true
        await mutateAsync()
        toast.success("Subscription has been reactivated.")
      }
      catch (e) {
        toast.error(e)
      }
      finally {
        inProgress.value = false
      }
    }
  }
  const changeSubscription = () => {
    const { mutateAsync } = useMutation({
      async mutationFn({ planId, requiredUserInformation }) {
        await axios.patch(`/partner/subscriptions/checkout_api/${toValue(id)}`,
          { plan: planId },
        )
        await axios.post(`/partner/subscriptions/${toValue(id)}/billing_details`,
          { customerDetails: requiredUserInformation },
        )
        const { data } = await axios.get(`/partner/subscriptions/${toValue(id)}`)
        return data
      },
      onSuccess,
    })

    return async (planId, requiredUserInformation) => {
      try {
        inProgress.value = true
        // Needed to prevent partners to change subscription when stack is not in expected state.
        const stackName = currentSubscription.value.stackName
        await axios.post(`/partner/stacks/${stackName}/status`,
          { accepted_states: ["CREATE_COMPLETE", "UPDATE_COMPLETE"] },
        )
      }
      catch {
        inProgress.value = false
        toast.error("The instance is currently being created or updated, please wait a few minutes.")
        return
      }
      try {
        await mutateAsync({ planId, requiredUserInformation })
        toast.success("Subscription has been changed and customer information has been updated.")
      }
      catch (e) {
        toast.error(e)
      }
      finally {
        inProgress.value = false
      }
    }
  }
  const requestSubscriptionChange = async (ticket, requiredUserInformation) => {
    try {
      inProgress.value = true
      await axios.post("/partner/subscriptions/ticket", ticket)
      await axios.post(`/partner/subscriptions/${toValue(id)}/billing_details`,
        { customerDetails: requiredUserInformation },
      )
      toast.success("Subscription change has been requested and customer information has been updated.")
    }
    catch (e) {
      toast.error(e)
    }
    finally {
      inProgress.value = false
    }
  }

  const changeSubdomain = () => {
    const { mutateAsync } = useMutation({
      async mutationFn(subdomain) {
        await axios.put(`/partner/subscriptions/${toValue(id)}/subdomain`, { subdomain })
        const { data } = await axios.get(`/partner/subscriptions/${toValue(id)}`)
        return data
      },
      onSuccess,
    })

    return async (subdomain) => {
      try {
        inProgress.value = true
        // Needed to prevent partners to change subscription when stack is not in expected state.
        const stackName = currentSubscription.value.stackName
        await axios.post(`/partner/stacks/${stackName}/status`, { accepted_states: ["CREATE_COMPLETE", "UPDATE_COMPLETE"] })
      }
      catch {
        inProgress.value = false
        toast.error("The instance is currently being created or updated, please wait a few minutes.")
        return
      }
      try {
        inProgress.value = true
        await mutateAsync(subdomain)
        toast.success(`The subdomain has been successfully changed to ${subdomain}`)
      }
      catch (e) {
        toast.error(e)
      }
      finally {
        inProgress.value = false
      }
    }
  }

  return {
    isFetching,
    isCompleteStack,
    isFailedStack,
    isBlockedSanctionReviewStack,
    inProgressStack,
    inUnknownStack,
    inProgress,
    subscriptions: normalizedSubscriptions,
    currentSubscription,
    changeOrderNumber,
    refetchSubscriptions: refetch,
    changeSubscription: changeSubscription(),
    cancelSubscription: cancelSubscription(),
    restoreSubscription: restoreSubscription(),
    requestSubscriptionChange,
    changeSubdomain: changeSubdomain(),
  }
}

export {
  useSubscriptions,
}
